import React, { useState } from 'react';
import { deleteEvent } from '../services/eventsService';
import { getDisplayErrorMessage } from '../services/apiError';
import { useNavigate } from 'react-router-dom';
import RichTextDisplay from './RichTextDisplay';
import ErrorMessage from './ErrorMessage';

const EventDetails = ({ event, isOwner, onDelete, allEvents, isStudyMode }) => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  if (!event) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-50 border-y border-gray-200">
        <p className="text-gray-400">Details</p>
      </div>
    );
  }

  const studyStatus = event.studyStatus;
  const modification = event.modification;

  const renderField = (originalValue, fieldName, index) => {
    if (!modification || !(fieldName in modification)) {
      return originalValue;
    }

    const modValue = modification[fieldName];
    let isModified = false;

    if (Array.isArray(modValue) && index !== undefined) {
      isModified = modValue[index] === null;
    } else {
      isModified = modValue === null || (Array.isArray(modValue) && modValue.every(v => v === null));
    }

    if (isModified) {
      if (studyStatus === 'learn') {
        return (
          <span className="bg-yellow-100 px-1 rounded font-semibold text-black">
            {originalValue}
          </span>
        );
      } else if (studyStatus === 'review') {
        return (
          <span className="bg-gray-300 text-transparent select-none px-1 rounded">
            {originalValue}
          </span>
        );
      }
    }

    return originalValue;
  };

  const renderDates = () => {
    if (!event.dates || event.dates.length === 0) return '';
    
    if (event.dates.length === 1) {
      return renderField(event.dates[0], 'dates', 0);
    }
    
    return (
      <>
        {renderField(event.dates[0], 'dates', 0)}
        {' — '}
        {renderField(event.dates[event.dates.length - 1], 'dates', event.dates.length - 1)}
      </>
    );
  };

  const handleDeleteEventClick = async () => {
    try {
      await deleteEvent(event.id);
      onDelete(event.id);
    } catch (err) {
      setError(getDisplayErrorMessage(err));
    }
  };

  const handleEditEventClick = () => {
    navigate(`/editEvent/${event.id}`, { state: { event, allEvents } });
  }

  const isFullDescriptionModified = modification && 'fullDescription' in modification && modification.fullDescription === null;
  const shouldBlurDescription = isFullDescriptionModified && studyStatus === 'review';

  return (
    <div className="h-64 bg-white border-y border-gray-200 relative">
      <ErrorMessage message={error} onDismiss={() => setError(null)} />
      <div className="h-full p-4 px-14 overflow-y-auto flex flex-row">
        <img className="rounded-lg max-w-[50%] object-contain" src={ event.image } alt={ event.name }></img>
        <div className="ml-6">
          <p className={`${isStudyMode ? 'text-black text-xl' : 'text-stone-400'} text-sm mb-1`}>
            {renderDates()}
          </p>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {renderField(event.name, 'name')}
          </h2>
          <div className={
            isFullDescriptionModified && studyStatus === 'learn'
              ? 'bg-yellow-50 p-2 rounded relative'
              : isFullDescriptionModified && studyStatus === 'review'
              ? 'relative'
              : ''
          }>
            {shouldBlurDescription && (
              <div className="absolute inset-0 bg-gray-300 rounded z-10"></div>
            )}
            <RichTextDisplay content={event.fullDescription} className="text-sm text-gray-600" />
          </div>
        </div>
      </div>
      {isOwner && (
        <>
          <button
            onClick={handleEditEventClick}
            className="bg-white w-8 h-8 rounded-full z-50 absolute right-2 top-2 flex items-center justify-center shadow-lg hover:bg-blue-100 hover:border-2 hover:border-blue-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-700">
              <path d="M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z" />
              <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0010 3H4.75A2.75 2.75 0 002 5.75v9.5A2.75 2.75 0 004.75 18h9.5A2.75 2.75 0 0017 15.25V10a.75.75 0 00-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5z" />
            </svg>
          </button>
        
          <button
            onClick={handleDeleteEventClick}
            className="absolute top-1/2 -translate-y-1/2 right-2 bg-white w-8 h-8 rounded-full z-50 flex items-center justify-center shadow-lg hover:bg-red-100 hover:border-2 hover:border-red-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
        </>
        )
      }
    </div>
  );
};

export default EventDetails;